import { components } from "@certn/api/generated-schemas/client-portal";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";

import { CheckCardError } from "./CheckCardError";
import { useCheckList } from "./useCheckList";
import { OrderingCheckCard } from "../../../../components/ordering-check-card";
import { CheckData } from "../../types";
interface CheckListProps {
  availableChecks: CheckData[];
  order: any;
  bundleChecks: string[];
  handleCheckSelect: (check: CheckData) => void;
  handleUpdateArguments: (
    checkArguments: Record<string, unknown>,
    check: CheckData,
    selectedOptions:
      | components["schemas"]["UmbrellaCheckSelectOption"][]
      | undefined
  ) => void;
  handleUpdateCheckAddOns?: (
    checkType: string,
    checkArguments: Record<string, unknown>
  ) => void;
  group: string | null;
  isQuickscreen?: boolean;
}

export function CheckList({
  availableChecks,
  order,
  bundleChecks,
  handleCheckSelect,
  handleUpdateArguments,
  handleUpdateCheckAddOns,
  group,
  isQuickscreen,
}: CheckListProps) {
  const checkList = useCheckList({
    availableChecks,
    order,
    bundleChecks,
    handleCheckSelect,
    handleUpdateArguments,
    handleUpdateCheckAddOns,
    group,
  });

  return (
    <div className={styles.container}>
      {checkList.map(
        ({
          check,
          checkHasError,
          checkIsRequired,
          checkIsStandaloneDisabled,
          checkIsInBundle,
          checkIsSelected,
          bundledSelectedArg,
          currentSelection,
          checkArguments,
          group,
          handleCheckSelect,
          onUpdateSelection,
          handleUpdateCheckAddOns,
        }) => (
          <OrderingCheckCard
            errors={
              checkIsSelected && (
                <CheckCardError
                  error={check.check_has_ordering_error_for_customer_group}
                />
              )
            }
            isDisabled={checkIsInBundle || checkIsStandaloneDisabled}
            key={check.type}
            checkOrderingError={
              check.check_has_ordering_error_for_customer_group
            }
            type={check.type}
            name={check.name}
            price={check.price}
            priceIsCatalogMinimum={check.price_is_catalog_minimum}
            currency={check.currency}
            checkChildren={check.children || []}
            turnaroundTime={check.estimated_turnaround_time}
            disclaimersIconList={check.disclaimers_icon_list}
            disclaimers={check.disclaimers}
            description={check.description}
            infoParagraphs={check.info_paragraphs}
            coverage={check.coverage ?? ""}
            keyFeatures={check.key_features}
            howItCanHelpYou={check.how_it_can_help_you}
            selectFieldDescription={check.select_field_descriptions}
            selectFieldEndpoint={check.select_field_endpoint}
            selectOptions={
              !checkIsInBundle
                ? check.select_options
                : bundledSelectedArg?.select_options ?? []
            }
            onUpdateSelection={onUpdateSelection}
            handleUpdateCheckAddOns={handleUpdateCheckAddOns}
            umbrellaChecksSupported={
              check.children?.filter((child) => {
                const errorType =
                  child.check_has_ordering_error_for_customer_group?.type;
                return errorType !== "error" && errorType !== "warning";
              }) || []
            }
            umbrellaChecksHeading={check.umbrella_child_checks_heading}
            isSelected={checkIsSelected}
            onPress={() => handleCheckSelect(check)}
            testID={slugify(check.type)}
            isRequiredCheck={checkIsRequired}
            width={480}
            selectedOptions={
              currentSelection
                ? Array.isArray(currentSelection)
                  ? currentSelection
                  : [currentSelection]
                : undefined
            }
            customerGroup={group || undefined}
            selectFieldInformation={check.select_field_information}
            checkArguments={checkArguments}
            isQuickscreen={isQuickscreen}
            addOnOptions={check.add_on_options}
          />
        )
      )}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
    "& > *:last-child": {
      flex: "0 0 49%",
    },
  }),
};
